import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCampaigns } from '../stores/campaigns'
import { TierGate } from '../components/TierGate'
import { Modal } from '@unvld/ui'
import { Plus, Mail, ArrowRight, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { toast } from 'sonner'

const STATUS_MAP = {
  draft: { label: 'Entwurf', cls: 'bg-slate-700 text-slate-300' },
  scheduled: { label: 'Geplant', cls: 'bg-blue-500/20 text-blue-400' },
  sending: { label: 'Wird gesendet', cls: 'bg-amber-500/20 text-amber-400' },
  sent: { label: 'Gesendet', cls: 'bg-emerald-500/20 text-emerald-400' },
  cancelled: { label: 'Abgebrochen', cls: 'bg-red-500/20 text-red-400' },
} as const

export function CampaignsPage() {
  const { campaigns, loading, fetchCampaigns, deleteCampaign } = useCampaigns()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; subject: string } | null>(null)

  useEffect(() => {
    fetchCampaigns()
  }, [])

  const confirmDelete = (id: string, subject: string) => {
    setDeleteTarget({ id, subject })
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteCampaign(deleteTarget.id)
      toast.success('Kampagne gelöscht')
    } catch {
      toast.error('Fehler beim Löschen')
    }
    setShowDeleteModal(false)
    setDeleteTarget(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Kampagnen</h1>
          <p className="text-slate-400 text-sm mt-1">Newsletter an deine Gäste versenden</p>
        </div>
        <Link
          to="/campaigns/new"
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neue Kampagne
        </Link>
      </div>

      <TierGate feature="campaigns">
      {loading && campaigns.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20 bg-[#131316] border border-white/5 rounded-xl">
          <Mail className="w-16 h-16 text-slate-700 mx-auto mb-4" />
          <h2 className="text-xl text-white font-semibold mb-2">Noch keine Kampagnen erstellt</h2>
          <p className="text-slate-400 mb-6">
            Erstelle deine erste Newsletter-Kampagne und erreiche deine Gäste.
          </p>
          <Link
            to="/campaigns/new"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Erste Kampagne erstellen
          </Link>
        </div>
      ) : (
        <div className="bg-[#131316] border border-white/5 rounded-xl overflow-auto max-h-[calc(100vh-12rem)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-[#131316]">
              <tr className="border-b border-white/5">
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">
                  Betreff
                </th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">
                  Status
                </th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">
                  Empfänger
                </th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">
                  Erstellt am
                </th>
                <th scope="col" className="w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {campaigns.map((campaign) => {
                const status =
                  STATUS_MAP[campaign.status as keyof typeof STATUS_MAP] || STATUS_MAP.draft
                return (
                  <tr
                    key={campaign.id}
                    className="hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/campaigns/${campaign.id}/edit`}
                        className="text-white hover:text-indigo-400 font-medium"
                      >
                        {campaign.subject || '(Kein Betreff)'}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.cls}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">
                      {campaign.recipient_count > 0 ? campaign.recipient_count : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {format(new Date(campaign.created_at), 'd. MMM yyyy', { locale: de })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => confirmDelete(campaign.id, campaign.subject)}
                            className="p-1.5 text-slate-600 hover:text-red-400 transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <Link
                          to={`/campaigns/${campaign.id}/edit`}
                          className="text-slate-600 hover:text-white transition-colors"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      </TierGate>

      {/* Delete Confirm Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null) }}
        title="Kampagne löschen"
        description="Diese Aktion kann nicht rückgängig gemacht werden."
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Kampagne &quot;{deleteTarget?.subject}&quot; wirklich löschen?
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={() => { setShowDeleteModal(false); setDeleteTarget(null) }}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
